import React, { useCallback, useMemo, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { get, size } from "lodash";
import {
  Button,
  Grid,
  Typography,
  TextField,
  Stack,
  Divider,
  FormControlLabel,
  Checkbox,
  Chip,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { color } from "../../utils/constant";
import { useSelector } from "react-redux";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Iconify from "../../components/iconify/iconify";
import { useUserMasterForm } from "../../data/talons/User_Master/useUser_MasterForm";
import {
  getDoctorList,
  getUserMasterData,
} from "../../data/selectors/User_Master/user_MasterForm.selector";
import { useBoolean } from "../../hooks/use-boolean";
import RHFAutocomplete from "../../components/shared/RHFAutocomplete";
import CustomPopover from "../../components/custom-popover/custom-popover";
import usePopover from "../../components/custom-popover/use-popover";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import { Box } from "@mui/system";

/*
 * Render Real - Estate Inquiry Form screen
 *
 *
 */

export default function UserMasterForm(props) {
  const { enqueueSnackbar } = useSnackbar();
  const editItem = useSelector(getUserMasterData);
  const doctorList = useSelector(getDoctorList);
  const {
    loader,
    loading,
    status,
    DoctorId,
    UserMasterEntrySchema,
    handleStatusComboChange,
    findDoctorFromLabel,
    fetchDoctorComboList,
    UserMasterEntry,
    UserMasterUpdate,
    handleFileChange,
  } = useUserMasterForm({
    existingStatus: get(editItem, "status", 0),
    existingDoctorName: get(editItem, "doctor_id", ""),
    existingImageUrl: { url: get(editItem, "image_url", "") },
  });

  const popover = usePopover();

  useEffect(() => {
    fetchDoctorComboList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultValues = useMemo(
    () => ({
      doctor_name: get(editItem, "doctor_name", ""),
      name: get(editItem, "name", ""),
      user_name: get(editItem, "user_name", ""),
      password: get(editItem, "password", ""),
      email_id: get(editItem, "email_id", ""),
      mobile_no: get(editItem, "mobile_no", ""),
    }),
    [editItem]
  );

  const [imageData, setImageData] = useState(
    size(editItem?.image_url) === 0 ? null : editItem.image_url
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    control,
    setValue,
  } = useForm({
    resolver: joiResolver(UserMasterEntrySchema),
    defaultValues,
  });

  const onUserSubmit = useCallback(async () => {
    try {
      const res =
        get(editItem, "id", null) === null
          ? await UserMasterEntry({
              ...getValues(),
            })
          : await UserMasterUpdate({
              ...getValues(),
              id: get(editItem, "id", null),
            });
      if (res === true) {
        props.postSubmit();
      } else {
        enqueueSnackbar("Problem While Saving UserMaster Data", {
          variant: "error",
        });
      }
    } catch (err) {
      console.log("errr", err);
    }
  }, [
    UserMasterEntry,
    UserMasterUpdate,
    editItem,
    enqueueSnackbar,
    getValues,
    props,
  ]);

  const fileUpload = (file) => {
    handleFileChange(file);
    popover.onClose();
    const reader = new FileReader();

    reader.onload = () => {
      setImageData(reader.result);
    };

    reader.readAsDataURL(file);
  };

  const onlyNumbers = (e) => {
    e.target.value = e.target.value.replace(/[^0-9]/g, "");

    if (e.target.value.toString().length === 0) return;
    return (e.target.value = Math.max(0, parseInt(e.target.value))
      .toString()
      .slice(0, 10));
  };
  const passwordBoolean = useBoolean();

  return (
    <React.Fragment>
      <Typography variant="h5" gutterBottom>
        User Master Form
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12}>
          <RHFAutocomplete
            borderRadius={"0px"}
            isRequired={true}
            name="doctor_name"
            label="Doctor Name"
            freeSolo
            data={doctorList || []}
            defaultValue={DoctorId}
            onInputChange={(value) => findDoctorFromLabel(value)}
            id={doctorList?.map((option) => option.id)}
            renderOption={(props, option) => (
              <>
                <li {...props} key={option}>
                  {option}
                </li>
              </>
            )}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  color="info"
                  variant="soft"
                />
              ))
            }
            control={control}
            setValue={setValue}
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <TextField
            required
            id="user_name"
            name="user_name"
            label="User Name"
            // onInput={(e) => onlyCharacters(e)}
            fullWidth={true}
            variant="outlined"
            size="small"
            inputProps={{
              maxLength: 50,
            }}
            {...register("user_name")}
            autoComplete="false"
            error={size(errors?.user_name?.message) > 0 ? true : false}
            helperText={
              size(errors?.user_name?.message) > 0
                ? errors?.user_name?.message
                : ""
            }
          />
        </Grid>
        <Grid item xs={8} sm={12}>
          <TextField
            required
            disabled={!!editItem?.id}
            id="password"
            name="password"
            type={passwordBoolean.value ? "text" : "password"}
            label="Password"
            fullWidth={true}
            variant="outlined"
            size="small"
            inputProps={{
              maxLength: 50,
            }}
            {...register("password")}
            autoComplete="new-password" // Prevent autofill
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={passwordBoolean.onToggle} edge="end">
                    <Iconify
                      icon={
                        passwordBoolean.value
                          ? "solar:eye-bold"
                          : "solar:eye-closed-bold"
                      }
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={size(errors?.password?.message) > 0 ? true : false}
            helperText={
              size(errors?.password?.message) > 0
                ? errors?.password?.message
                : ""
            }
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <TextField
            required
            id="name"
            name="name"
            label="Name"
            // onInput={(e) => onlyCharacters(e)}
            fullWidth={true}
            variant="outlined"
            size="small"
            inputProps={{
              maxLength: 50,
            }}
            {...register("name")}
            autoComplete="false"
            error={size(errors?.name?.message) > 0 ? true : false}
            helperText={
              size(errors?.name?.message) > 0 ? errors?.name?.message : ""
            }
          />
        </Grid>
        <Grid item xs={8} sm={12}>
          <TextField
            required
            id="email_id"
            name="email_id"
            label="Email"
            fullWidth={true}
            variant="outlined"
            size="small"
            inputProps={{
              maxLength: 50,
            }}
            {...register("email_id")}
            autoComplete="false"
            error={size(errors?.email_id?.message) > 0 ? true : false}
            helperText={
              size(errors?.email_id?.message) > 0
                ? errors?.email_id?.message
                : ""
            }
          />
        </Grid>
        <Grid item xs={8} sm={12}>
          <TextField
            required
            id="mobile_no"
            name="mobile_no"
            label="Mobile Number"
            fullWidth={true}
            onInput={(e) => onlyNumbers(e)}
            variant="outlined"
            size="small"
            inputProps={{
              maxLength: 50,
            }}
            {...register("mobile_no")}
            autoComplete="false"
            error={size(errors?.mobile_no?.message) > 0 ? true : false}
            helperText={
              size(errors?.mobile_no?.message) > 0
                ? errors?.mobile_no?.message
                : ""
            }
          />
        </Grid>
        {imageData === null ? (
          <Grid item xs={12} sm={6}>
            <IconButton
              className="p-0"
              style={{ width: "100%", borderRadius: "0px", padding: 0 }}
              color="primary"
            >
              <label
                htmlFor="upload"
                onMouseEnter={(e) => (e.target.style.padding = "1rem")}
                style={{
                  width: "100%",
                  height: "210px",
                  border: "2px dashed #1976d2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <>
                  <AddAPhotoIcon style={{ fontSize: "3rem" }} />
                  <input
                    accept="image/*"
                    onChange={(e) => fileUpload(e.target.files[0])}
                    type="file"
                    hidden={true}
                    id="upload"
                  />
                </>
              </label>
            </IconButton>
          </Grid>
        ) : (
          <Grid
            item
            xs={12}
            sm={6}
            sx={{ width: "100%", textAlign: "center", height: "210px" }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "end",
              }}
            >
              <IconButton
                className="iconbtn"
                onClick={popover.onOpen}
                sx={{
                  color: "blue",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 255, 0.1)", // on hover background
                  },
                }}
              >
                <Iconify icon="eva:more-vertical-fill" />
              </IconButton>
            </Box>
            <img
              className="uploadedImg"
              src={imageData}
              style={{
                marginBottom: "10px",
                width: "100%",
                height: "95%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "-35px",
              }}
            />

            <CustomPopover
              open={popover.open}
              onClose={popover.onClose}
              arrow="right-top"
              sx={{ width: 100, ml: 1, marginTop: "30px" }}
            >
              <IconButton
                style={{
                  width: "100%",
                  borderRadius: "0px",
                  fontSize: "16px",
                  fontWeight: "normal",
                }}
                color="primary"
                sx={{ padding: 0 }}
              >
                <label
                  htmlFor="uploadagain"
                  style={{
                    width: "100%",
                    padding: 7,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <Iconify icon="solar:pen-bold" />
                    <span style={{ marginLeft: "10px" }}>Edit</span>
                    <input
                      className="uploadInput"
                      accept="image/*"
                      onChange={(e) => fileUpload(e.target.files[0])}
                      // onClick={(e) => editedFileUpload(e.target)}
                      type="file"
                      hidden={true}
                      id="uploadagain"
                    />
                  </div>
                </label>
              </IconButton>
              <div
                onClick={() => setImageData(null)}
                style={{
                  display: "flex",
                  marginLeft: "5px",
                  alignItems: "center",
                  cursor: "pointer",
                  color: "red",
                }}
              >
                <Iconify icon="solar:trash-bin-trash-bold" />
                {/* <span style={{ marginLeft: "10px" }}>Delete</span> */}
              </div>
            </CustomPopover>
          </Grid>
        )}

        <Grid item xs={3} sm={3}>
          <Typography>
            <FormControlLabel
              sx={{ mt: -1 }}
              control={
                <Checkbox
                  sx={{
                    "&.Mui-checked": {
                      color: color.button,
                    },
                  }}
                  id="is_labour"
                  checked={status}
                  onChange={handleStatusComboChange}
                />
              }
              label="Active"
            />
          </Typography>
        </Grid>
        <Grid container direction="row">
          <Grid item xs={12} sm={12}>
            <Stack direction="row" justifyContent="end">
              <Button
                variant="contained"
                sx={{
                  height: 40,
                  borderRadius: 3,
                  m: 1,
                  backgroundColor: color.button,
                  "&:hover": {
                    backgroundColor: color.button,
                  },
                }}
                type="submit"
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit(onUserSubmit)();
                }}
              >
                Save
              </Button>
              <Button
                sx={{
                  height: 40,
                  borderRadius: 3,
                  m: 1,
                  borderColor: color.button,
                  color: color.button,
                  "&:hover": {
                    color: color.button,
                  },
                }}
                variant="outlined"
                onClick={(e) => {
                  e.preventDefault();
                  props.postSubmit();
                }}
              >
                Cancel
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}
